import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isAdmin } from "@/lib/permissions";  // eslint-disable-line no-unused-vars
import { useT, localizedField } from "@/lib/i18n";
import { ATLAS } from "@/constants/testIds";
import { ArrowLeft, MapPin, Sparkles, GraduationCap, Loader2 } from "lucide-react";  // eslint-disable-line no-unused-vars
import { toast, Toaster } from "sonner";
import AppellationsInText from "@/components/AppellationsInText";
import AtlasBlocksSection from "@/components/AtlasBlocksSection";
import BackButton from "@/components/BackButton";

export default function CepageDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [genBusy, setGenBusy] = useState(false);

  useEffect(() => {
    api.get(`/cepages/${slug}`).then((r) => setItem(r.data)).catch(() => setItem(null));
  }, [slug]);

  if (!item) return <div className="max-w-4xl mx-auto px-6 py-24 text-[#8B95A1]">{t("common.loading")}</div>;

  const generateCourse = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post("/admin/academy/generate", {
        source_type: "cepage",
        source_value: item.slug || slug,
      });
      toast.success(`Parcours « ${data.title} » généré 🎉 — Édite-le sur https://geovinum.io/admin`, { duration: 8000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Génération impossible.");
    } finally { setGenBusy(false); }
  };

  const history = localizedField(item, "history", lang);
  const aromatic = localizedField(item, "aromatic_profile", lang);
  const climate = localizedField(item, "climate", lang);
  const pairings = localizedField(item, "pairings", lang);

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-10 md:py-16">
      <BackButton fallback="/cepages" />

      <div className="mt-8">
        <span className="chip-pink">{item.color} · {item.origin}</span>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight text-[#1F3A5F] mt-4 font-medium">{item.name}</h1>
      </div>

      <div className="mt-12 space-y-10">
        <Block title={t("cepages.detail.history")}>{history}</Block>
        <Block title={t("cepages.detail.profile")}>{aromatic}</Block>
        <Block title={t("cepages.detail.climate")}>{climate}</Block>
        <div>
          <SectionTitle>{t("cepages.detail.regions")}</SectionTitle>
          <div className="flex flex-wrap gap-2 mt-3">
            {(item.regions || []).map((r) => (
              <span key={r} className="px-3 py-1.5 rounded-full border border-[#1F3A5F]/12 text-sm text-[#1F3A5F] bg-white">{r}</span>
            ))}
          </div>
        </div>
        <Block title={t("cepages.detail.pairings")}>{pairings}</Block>
      </div>

      <AppellationsInText text={[
        item.name, history, aromatic, climate, pairings,
        ...(item.regions || []),
      ].filter(Boolean).join(" ")} />

      <div className="mt-12 pt-8 border-t border-[#1F3A5F]/8 flex flex-wrap gap-3">
        <Toaster theme="light" />
        <button
          onClick={() => navigate(`/chat?q=${encodeURIComponent(`Parle-moi du ${item.name} en détail`)}`)}
          data-testid={ATLAS.detailMapBtn}
          className="btn-primary"
        >
          <Sparkles className="w-4 h-4" /> {t("cepages.detail.askAi")}
        </button>
        <Link to="/appellations" className="btn-secondary">
          <MapPin className="w-4 h-4" /> {t("cepages.detail.viewMap")}
        </Link>
        <Link to="/pro" data-testid={ATLAS.detailEmbedBtn} className="btn-secondary">
          {t("cepages.detail.embed")}
        </Link>
      </div>

      <AtlasBlocksSection entityType="cepage" slug={item.slug || slug} />
    </div>
  );
}

const SectionTitle = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-semibold mb-2">{children}</div>
);
const Block = ({ title, children }) => (
  <div>
    <SectionTitle>{title}</SectionTitle>
    <p className="text-base md:text-lg text-[#1F3A5F] leading-relaxed font-light">{children}</p>
  </div>
);
